import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { useRef } from "react";

const POINTS = [
  "Organized, transparent product information",
  "Professional, consistent presentation",
  "Careful handling and discreet packaging",
  "Clear research-only positioning",
];

export default function QualityTransparency() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section ref={ref} className="bg-[#F7F4EF]" aria-label="Quality and transparency">
      <div className="container-rnv py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal className="order-2 lg:order-1">
            <span className="overline">Quality &amp; Transparency</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1E2A44] mt-3 leading-tight">
              Research Begins with Confidence
            </h2>
            <p className="mt-6 text-[#262E3D] leading-relaxed">
              Renova is built around clarity. We focus on organized product information,
              professional presentation, and careful handling — so that every material is
              catalogued, identified, and prepared for the demands of a research setting.
            </p>
            <ul className="mt-8 space-y-3.5">
              {POINTS.map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm text-[#262E3D]">
                  <span className="w-5 h-5 rounded-full border border-[#C8A96A] flex items-center justify-center text-[#C8A96A]">
                    <Check size={12} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
            <Link to="/quality-standards" className="btn-navy mt-10" data-testid="quality-learn-btn">
              Learn About Our Standards <ArrowRight size={16} />
            </Link>
          </Reveal>

          <Reveal delay={0.1} className="order-1 lg:order-2">
            <div className="relative overflow-hidden rounded-sm border border-[#1E2A44]/10">
              <motion.img
                src="https://images.unsplash.com/photo-1656331797721-b593b8f00297?auto=format&fit=crop&w=1100&q=70"
                alt="Abstract molecular structure render representing precise research materials"
                width="1100"
                height="1300"
                loading="lazy"
                className="w-full h-[420px] md:h-[560px] object-cover"
                style={reduce ? undefined : { y: imgY, scale: 1.08 }}
              />
              <div className="absolute bottom-5 left-5 bg-[#1E2A44] text-[#F7F4EF] text-xs uppercase tracking-[0.22em] px-4 py-2 rounded-sm">
                Precision · Clarity · Care
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
